import { Documento } from '../services/documentoService';

export type ChecklistItem = {
  key: string;
  label: string;
  ok: boolean;
  statusText: string;
  uploadTipo?: string;
  uploadObservacao?: string;
  waived?: boolean;
  waivedIcon?: 'farm' | 'dog';
  documentoId?: number;
};

export type ChecklistGroup = {
  title: string;
  keys: string[];
  columns?: 1 | 2;
};

export const CHECKLIST_GROUPS: ChecklistGroup[] = [
  { title: 'Documentação legal', keys: ['CR', 'CRAF', 'GT', 'SIMAF'], columns: 2 },
  { title: 'Fazenda', keys: ['autorizacao'], columns: 1 },
  { title: 'Cães', keys: ['vacina', 'laudo'], columns: 1 },
];

export const getChecklistSectionId = (item: ChecklistItem): string | null => {
  if (item.uploadTipo) {
    return `doc-section-${item.uploadTipo}`;
  }
  if (item.key === 'autorizacao') {
    return 'doc-section-Autorização Fazenda';
  }
  return null;
};

const IS_OWNER_STORAGE_KEY = 'propampa_doc_is_owner';
const WITH_DOGS_STORAGE_KEY = 'propampa_doc_with_dogs';
/** @deprecated migrado para withDogs — leitura apenas para compatibilidade */
const NO_DOG_STORAGE_KEY = 'propampa_doc_no_dog';

export const documentChecklistStorageKeys = {
  isOwner: IS_OWNER_STORAGE_KEY,
  withDogs: WITH_DOGS_STORAGE_KEY,
  /** @deprecated use withDogs */
  noDog: NO_DOG_STORAGE_KEY,
} as const;

export const readHuntWithDogsPreference = (): boolean | null => {
  if (typeof localStorage === 'undefined') return null;
  const storedWithDogs = localStorage.getItem(WITH_DOGS_STORAGE_KEY);
  if (storedWithDogs !== null) {
    return storedWithDogs === 'true';
  }
  const storedNoDog = localStorage.getItem(NO_DOG_STORAGE_KEY);
  if (storedNoDog !== null) {
    return storedNoDog !== 'true';
  }
  return null;
};

export const isDocValid = (doc: Documento) => doc.status === 'Válido';

export const isVaccinationDoc = (doc: Documento) => {
  const text = `${doc.nome_arquivo} ${doc.observacoes || ''}`.toLowerCase();
  return doc.tipo_documento === 'Outros' && (text.includes('vacina') || text.includes('carteira'));
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('pt-BR');
  } catch {
    return dateString;
  }
};

export const buildChecklistItems = (
  documentos: Documento[],
  isOwner: boolean,
  huntWithDogs: boolean
): ChecklistItem[] => {
  const byTipo = (tipo: string) => documentos.find((doc) => doc.tipo_documento === tipo);

  const fromTipo = (label: string, tipo: string, key?: string): ChecklistItem => {
    const itemKey = key ?? tipo;
    const doc = byTipo(tipo);
    if (doc && isDocValid(doc)) {
      return {
        key: itemKey,
        label,
        ok: true,
        statusText: `Válido até ${formatDate(doc.data_validade)}`,
        uploadTipo: tipo,
        documentoId: doc.id,
      };
    }
    if (doc) {
      return {
        key: itemKey,
        label,
        ok: false,
        statusText: doc.status === 'Vencido' ? 'Vencido — envie novo' : 'Aguardando validação',
        uploadTipo: tipo,
        documentoId: doc.id,
      };
    }
    return {
      key: itemKey,
      label,
      ok: false,
      statusText: 'Não enviado — clique para enviar',
      uploadTipo: tipo,
    };
  };

  const vacinaDoc = documentos.find((doc) => isVaccinationDoc(doc));

  const vacinaItem: ChecklistItem = !huntWithDogs
    ? {
        key: 'vacina',
        label: 'Carteira de vacinação',
        ok: true,
        waived: true,
        waivedIcon: 'dog',
        statusText: 'Dispensado — caça sem cachorro',
      }
    : (() => {
        if (vacinaDoc && isDocValid(vacinaDoc)) {
          return {
            key: 'vacina',
            label: 'Carteira de vacinação',
            ok: true,
            statusText: `Válido até ${formatDate(vacinaDoc.data_validade)}`,
            uploadTipo: 'Outros',
            uploadObservacao: 'vacina',
            documentoId: vacinaDoc.id,
          };
        }
        if (vacinaDoc) {
          return {
            key: 'vacina',
            label: 'Carteira de vacinação',
            ok: false,
            statusText:
              vacinaDoc.status === 'Vencido' ? 'Vencido — envie novo' : 'Aguardando validação',
            uploadTipo: 'Outros',
            uploadObservacao: 'vacina',
            documentoId: vacinaDoc.id,
          };
        }
        return {
          key: 'vacina',
          label: 'Carteira de vacinação',
          ok: false,
          statusText: 'Não enviado — clique para enviar',
          uploadTipo: 'Outros',
          uploadObservacao: 'vacina',
        };
      })();

  const autorizacaoItem: ChecklistItem = isOwner
    ? {
        key: 'autorizacao',
        label: 'Autorização da fazenda',
        ok: true,
        waived: true,
        waivedIcon: 'farm',
        statusText: 'Dispensado — proprietário da fazenda',
      }
    : fromTipo('Autorização da fazenda', 'Autorização Fazenda', 'autorizacao');

  const laudoItem: ChecklistItem = !huntWithDogs
    ? {
        key: 'laudo',
        label: 'Laudo do veterinário',
        ok: true,
        waived: true,
        waivedIcon: 'dog',
        statusText: 'Dispensado — caça sem cachorro',
      }
    : fromTipo('Laudo do veterinário', 'Laudo Veterinario', 'laudo');

  return [
    fromTipo('CR', 'CR'),
    fromTipo('CRAF', 'CRAF'),
    fromTipo('GT', 'GT'),
    fromTipo('SIMAF', 'SIMAF'),
    autorizacaoItem,
    vacinaItem,
    laudoItem,
  ];
};
